import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { api } from '../services/api';
import type { Agent, LoginRequest } from '../types';

interface AuthContextType {
  agent: Agent | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  updateAgent: (agent: Agent) => void;
  isAdmin: () => boolean;
  isDepartmentManager: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [agent, setAgent] = useState<Agent | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    const storedAgent = localStorage.getItem('agent');

    if (token && storedAgent) {
      try {
        setAgent(JSON.parse(storedAgent));
      } catch (error) {
        console.error('Failed to parse stored agent:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('agent');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (credentials: LoginRequest) => {
    const response = await api.login(credentials);
    localStorage.setItem('token', response.token);
    localStorage.setItem('agent', JSON.stringify(response.agent));
    setAgent(response.agent);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('agent');
    setAgent(null);
  };

  const updateAgent = (updatedAgent: Agent) => {
    setAgent(updatedAgent);
    localStorage.setItem('agent', JSON.stringify(updatedAgent));
  };

  const isAdmin = () => agent?.role === 'ADMIN';

  const isDepartmentManager = () => agent?.role === 'DEPARTMENT_MANAGER' || agent?.role === 'ADMIN';

  return (
    <AuthContext.Provider
      value={{
        agent,
        isAuthenticated: !!agent,
        isLoading,
        login,
        logout,
        updateAgent,
        isAdmin,
        isDepartmentManager,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
